import {common, priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {getAdditionalProps} from '../../services/proto/core/loginProps';
import {formsStore} from '../../_legacy/Form/stores/FormsStore';
import {defaultRecordsLimit, PROJECTS_TARGET_COL_NAME, numOfDefInitialCols} from './constants';
import {callServer} from '../../services/proto/core/server';
import {ServerMethods} from '../../services/proto/core/constants';
import {FormData} from './components/DialogHeader';

const AttachmentOptions = priority.netitems.form.AttachmentOptions;

const destructValueObj = (value?: common.IConstant) => {
    return {
        Type: Object.keys(value)[0],
        Value: Object.values(value)[0]
    };
};

const mapRecords = (
    records: priority.netitems.form.IRecord[],
    cols: priority.netitems.form.IColumn[]
) => {
    return records.map(record => {
        return record.Fields.map(item => {
            const col = cols.find(col => Number(col.ID) === Number(item.ColumnID));
            const {Type, Value} = destructValueObj(item.Value);

            return {
                ...item,
                Value,
                Type,
                IsStatus: col?.IsStatus,
                ColumnName: col?.Name,
                ColumnTitle: col?.Title,
                ColumnType: col?.Type,
                Dir: col?.Type === 2 ? 'rtl' : 'ltr'
            };
        });
    });
};
export const fetchSubFormInfo = async (subFormData: FormData, Keys, Session: string) => {
    const {subFormName} = subFormData;

    const form = await callServer(
        wcfclient.subform,
        new priority.netitems.form.SubformRq({
            Properties: getAdditionalProps(),
            Fname: subFormName,
            Session,
            Keys
        }),
        ServerMethods.SubformRq
    );

    const message = form.Messages[0]?.MType;

    if (message === 2 || message === 3) {
        return null;
    }

    let cols = formsStore.getMoreInfoCachedCol(subFormName);
    if (!cols) {
        cols = await callServer(
            wcfclient.columns,
            new priority.netitems.form.ColumnsRq({
                Session: form.Session,
                Properties: getAdditionalProps()
            }),
            ServerMethods.ColumnsRq
        );

        formsStore.setMoreInfoCachedCol(subFormName, cols);
    }

    const Attachments = subFormData.hasAttachment
        ? AttachmentOptions.Attach_Data
        : AttachmentOptions.Attach_NoData;

    const mandatoryColumn = cols.Columns.find(col => col.Mandatory);
    const rows = await callServer(
        wcfclient.recordsFetch,
        new priority.netitems.form.FetchRq({
            Session: form.Session,
            Properties: getAdditionalProps(),
            Top: defaultRecordsLimit,
            Filters: [
                {
                    Condition: [
                        {
                            ColumnID: mandatoryColumn?.ID || cols.Columns[0]?.ID,
                            Operator: priority.netitems.shared.ComparisonOperator.Eq,
                            Values: ['*']
                        }
                    ]
                }
            ],
            Attachments
        }),
        ServerMethods.FetchRq
    );

    await callServer(
        wcfclient.end,
        new priority.netitems.form.FetchRq({
            Session: form.Session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.FetchRq
    );

    return {
        ...subFormData,
        formTitle: form.Title,
        fields: mapRecords(rows.Records, cols.Columns),
        cols: cols.Columns
    };
};

const fetchInfo = async (params: FormData & {zoomName: string}) => {
    const {fetchValue, subFormName, formName, hasAttachment, zoomName, targetColName} = params;
    const company = formsStore.currentForm.formCompanyName;
    try {
        const cacheName = `${formName}_${subFormName}_${company}`;
        let form; //formsStore.getMoreInfoCachedForm(cacheName);
        if (!form) {
            form = await callServer(
                wcfclient.start,
                new priority.netitems.form.StartRq({
                    Fname: formName,
                    Properties: getAdditionalProps()
                }),
                ServerMethods.StartRq
            );

            formsStore.setMoreInfoCachedForm(cacheName, form);
        }

        const message = form.Messages[0]?.MType;

        if (message === 2 || message === 3) {
            return null;
        }

        let cols = formsStore.getMoreInfoCachedCol(formName);
        if (!cols) {
            cols = await callServer(
                wcfclient.columns,
                new priority.netitems.form.ColumnsRq({
                    Session: form.Session,
                    Properties: getAdditionalProps()
                }),
                ServerMethods.ColumnsRq
            );

            formsStore.setMoreInfoCachedCol(formName, cols);
        }

        const Attachments = hasAttachment
            ? AttachmentOptions.Attach_Data
            : AttachmentOptions.Attach_NoData;

        const targetName = targetColName || zoomName;

        const getColumnId = () => {
            let id = cols.Columns.find(col => col.Name === targetName)?.ID;
            const projectsTargetColName = PROJECTS_TARGET_COL_NAME[targetName];
            if (!id && projectsTargetColName) {
                id = cols.Columns.find(col => col.Name === projectsTargetColName)?.ID;
            }
            return id;
        };

        const Filters = [
            {
                Condition: [
                    {
                        ColumnID: getColumnId(),
                        Operator: priority.netitems.shared.ComparisonOperator.Eq,
                        Values: [fetchValue]
                    }
                ]
            }
        ];

        const fieldsToFetch: (number | Long)[] = cols.Columns.filter(col => {
            if (params.selectedFields?.includes(col.Name) || col.Key) return col.ID;
        }).map(col => col.ID);

        const res = await callServer(
            wcfclient.recordsFetch,
            new priority.netitems.form.FetchRq({
                Session: form.Session,
                Properties: getAdditionalProps(),
                Filters,
                Attachments,
                Select: fieldsToFetch
            }),
            ServerMethods.FetchRq
        );

        const resMessage = res.Messages[0]?.MType;

        if (resMessage === 2 || resMessage === 3) {
            formsStore.clearMoreInfoCachedForm(cacheName);
        }

        const records = res.Records;
        const mappedFields = mapRecords(records, cols.Columns);

        const mainForm = {
            ...params,
            formTitle: form.Title,
            fields: mappedFields,
            cols: cols.Columns
        };

        const KeyCols = cols.Columns.filter(it => it.Key);
        const Keys = records[0]?.Fields.filter(it =>
            KeyCols.find(col => Number(col.ID) === Number(it.ColumnID))
        );

        const subForms = await callServer(
            wcfclient.subforms,
            new priority.netitems.form.SubformsRq({
                Session: form.Session,
                Properties: getAdditionalProps()
            }),
            ServerMethods.SubformsRq
        );
        const isSubformAvailable = subFormName
            ? subForms.SubForms.find(subForm => subForm.Ename === subFormName)
            : false;

        if (subFormName && isSubformAvailable) {
            const subFormRes = await fetchSubFormInfo(params, Keys, form.Session);
            if (!subFormRes) {
                return null;
            }
            const selectedFields = subFormRes.selectedFields;
            if (!selectedFields) {
                const firstCols = subFormRes.cols?.slice(0, numOfDefInitialCols);
                subFormRes.selectedFields = firstCols?.map(col => col.Name);
            }
            return subFormRes;
        } else if (subFormName) {
            return {...mainForm, isEmpty: true};
        }

        const selectedFields = mainForm.selectedFields;
        if (!selectedFields) {
            const firstCols = mainForm.cols.slice(0, numOfDefInitialCols);
            mainForm.selectedFields = firstCols.map(col => col.Name);
        }

        return mainForm;
    } catch (e) {
        const cacheName = `${formName}_${subFormName}`;
        formsStore.clearMoreInfoCachedForm(cacheName);

        console.error(e);
        throw Error(e as string);
    }
};

export const getFieldRelatedData = async (
    forms: Array<FormData>,
    reqParams: {fetchValue: string; zoomName: string}
) => {
    const {currentForm} = formsStore;
    const allFieldsReady = currentForm?.currentFieldMoreInfo?.every(form => form.fetchValue);

    if (allFieldsReady) {
        return currentForm.currentFieldMoreInfo;
    }

    const res = await Promise.all(
        forms.map(form => {
            if (form.fetchValue) {
                return form;
            } else {
                return fetchInfo({...reqParams, ...form});
            }
        })
    );

    const filteredRes = res.filter(Boolean).filter(form => !form.isEmpty); // Show only available forms #19002 & EP-29351;

    currentForm.setCurrentFieldMoreInfo(filteredRes);

    return filteredRes;
};

export const getFieldRelatedDataByForm = async (
    form: FormData,
    reqParams: {fetchValue: string; zoomName: string},
    entityId: string
) => {
    const {currentForm} = formsStore;
    const res = await fetchInfo({...reqParams, ...form});

    const updatedFieldMoreInfo = currentForm.currentFieldMoreInfo.map(form =>
        form.entityId === entityId ? res : form
    );
    return updatedFieldMoreInfo;
};
